import {
  apiErrorSchema,
  type ApiError,
  type AppLocale,
} from "@metaadsmanager/tk-master-contracts";
import type { z } from "zod";

import {
  NetworkRequestError,
  RequestTimeoutError,
} from "../errors/request-failure";

/** 与 TikTok Edge 浏览器请求对齐的默认超时。 */
export const API_CLIENT_DEFAULT_TIMEOUT_MS = 30_000;

type ApiClientOptions = {
  baseUrl: string;
  fetcher?: (request: Request) => Promise<Response>;
  getLocale?: () => AppLocale;
  createRequestId?: () => string;
  /** 默认请求超时；单次可被 RequestInitOptions.timeoutMs 覆盖。 */
  defaultTimeoutMs?: number;
};

/** 单次请求可选：合并 AbortSignal + 超时。 */
export type ApiRequestOptions = {
  signal?: AbortSignal;
  timeoutMs?: number;
};

/** 保留服务端错误 envelope 与 HTTP 状态的客户端异常。 */
export class ApiClientError extends Error {
  readonly payload: ApiError;
  readonly status: number;

  constructor(payload: ApiError, status: number) {
    super(payload.error.message);
    this.name = "ApiClientError";
    this.payload = payload;
    this.status = status;
  }
}

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, "");

const syntheticUnavailableError = (
  requestId: string,
  _status: number,
): ApiError => ({
  error: {
    code: "SERVICE_UNAVAILABLE",
    message: "Service unavailable",
    messageKey: "service.unavailable",
  },
  requestId,
});

/**
 * 创建使用 Cookie 会话、JSON envelope 与运行时 Schema 校验的 FC API 客户端。
 */
export const createApiClient = ({
  baseUrl,
  fetcher = fetch,
  getLocale,
  createRequestId = () => crypto.randomUUID(),
  defaultTimeoutMs = API_CLIENT_DEFAULT_TIMEOUT_MS,
}: ApiClientOptions) => {
  const request = async <T>(
    method: "GET" | "POST" | "PUT" | "DELETE",
    path: string,
    schema: z.ZodType<T>,
    body: unknown,
    contentType?: string,
    options?: ApiRequestOptions,
  ): Promise<T> => {
    const requestId = createRequestId();
    const headers = new Headers({ Accept: "application/json" });
    if (getLocale) headers.set("Accept-Language", getLocale());

    if (body !== undefined) {
      headers.set("Content-Type", contentType ?? "application/json");
    }

    const timeoutMs = options?.timeoutMs ?? defaultTimeoutMs;
    const controller = new AbortController();
    const external = options?.signal;
    const onExternalAbort = () => controller.abort();

    if (external?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }
    external?.addEventListener("abort", onExternalAbort);

    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    let response: Response;
    try {
      response = await fetcher(
        new Request(`${trimTrailingSlash(baseUrl)}${path}`, {
          method,
          headers,
          credentials: "include",
          signal: controller.signal,
          body:
            body === undefined
              ? undefined
              : contentType
                ? (body as BodyInit)
                : JSON.stringify(body),
        }),
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        // React Query 取消：原样抛出，勿当成超时错误。
        if (external?.aborted) throw error;
        throw new RequestTimeoutError(requestId);
      }
      if (error instanceof NetworkRequestError || error instanceof RequestTimeoutError) {
        throw error;
      }
      throw new NetworkRequestError(requestId);
    } finally {
      clearTimeout(timeoutId);
      external?.removeEventListener("abort", onExternalAbort);
    }

    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      throw new ApiClientError(
        syntheticUnavailableError(requestId, response.status || 503),
        response.status || 503,
      );
    }

    if (!response.ok) {
      throw new ApiClientError(apiErrorSchema.parse(payload), response.status);
    }

    try {
      return schema.parse(payload);
    } catch (error) {
      const zodIssues =
        error &&
        typeof error === "object" &&
        "issues" in error &&
        Array.isArray((error as { issues: unknown }).issues)
          ? (error as { issues: unknown[] }).issues
          : undefined;
      console.error(
        "[apiClient] schema 校验失败：后端返回不符合契约",
        {
          path,
          requestId,
          status: response.status,
          payload,
          error:
            error instanceof Error
              ? {
                  name: error.name,
                  message: error.message,
                  ...(zodIssues ? { issues: zodIssues } : {}),
                }
              : error,
        },
      );
      throw new ApiClientError(
        syntheticUnavailableError(requestId, response.status || 503),
        response.status || 503,
      );
    }
  };

  return {
    get: <T>(
      path: string,
      schema: z.ZodType<T>,
      options?: ApiRequestOptions,
    ) => request("GET", path, schema, undefined, undefined, options),
    post: <T>(
      path: string,
      body: unknown,
      schema: z.ZodType<T>,
      options?: ApiRequestOptions,
    ) => request("POST", path, schema, body, undefined, options),
    put: <T>(
      path: string,
      body: unknown,
      schema: z.ZodType<T>,
      options?: ApiRequestOptions,
    ) => request("PUT", path, schema, body, undefined, options),
    delete: <T>(
      path: string,
      schema: z.ZodType<T>,
      options?: ApiRequestOptions,
    ) => request("DELETE", path, schema, undefined, undefined, options),
    putBinary: <T>(
      path: string,
      body: Blob,
      schema: z.ZodType<T>,
      options?: ApiRequestOptions,
    ) => request("PUT", path, schema, body, body.type, options),
  };
};

/** FC API 客户端实例类型。 */
export type ApiClient = ReturnType<typeof createApiClient>;
